var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import useComponentSize from "@rehooks/component-size";
import React, { Fragment, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, useReducer } from "react";
import { createRoot } from 'react-dom/client';
import interpolator from "color-interpolate";
import colormap from "colormap";
import { flattenSVG } from "flatten-svg";
import { PaperSize } from "./paper-size";
import { Device, Plan, defaultPlanOptions, XYMotion, PenMotion } from "./planning";
import { formatDuration } from "./util";
import PlanWorker from "./plan.worker";
import "./style.css";
import pathJoinRadiusIcon from "./icons/path-joining radius.svg";
import pointJoinRadiusIcon from "./icons/point-joining radius.svg";
import rotateDrawingIcon from "./icons/rotate-drawing.svg";
import { EBB } from "./ebb";
const defaultVisualizationOptions = {
    penStrokeWidth: 0.2,
    colorPathsByStrokeOrder: false,
};
const initialState = {
    connected: true,
    paused: false,
    deviceInfo: null,
    // UI state
    planOptions: defaultPlanOptions,
    visualizationOptions: defaultVisualizationOptions,
    // Options used to produce the current value of |plan|.
    plannedOptions: null,
    // Info about the currently-loaded SVG.
    paths: null,
    groupLayers: [],
    strokeLayers: [],
    // While a plot is in progress, this will be the index of the current motion.
    progress: null,
};
// Update the initial state with previously persisted settings (if present)
const persistedPlanOptions = JSON.parse(window.localStorage.getItem("planOptions")) || {};
initialState.planOptions = Object.assign(Object.assign({}, initialState.planOptions), persistedPlanOptions);
initialState.planOptions.paperSize = new PaperSize(initialState.planOptions.paperSize.size);
const DispatchContext = React.createContext(null);
function reducer(state, action) {
    switch (action.type) {
        case "SET_PLAN_OPTION":
            return Object.assign(Object.assign({}, state), { planOptions: Object.assign(Object.assign({}, state.planOptions), action.value) });
        case "SET_VISUALIZATION_OPTION":
            return Object.assign(Object.assign({}, state), { visualizationOptions: Object.assign(Object.assign({}, state.visualizationOptions), action.value) });
        case "SET_DEVICE_INFO":
            return Object.assign(Object.assign({}, state), { deviceInfo: action.value });
        case "SET_PAUSED":
            return Object.assign(Object.assign({}, state), { paused: action.value });
        case "SET_PATHS":
            // eslint-disable-next-line no-case-declarations
            const { paths, strokeLayers, selectedStrokeLayers, groupLayers, selectedGroupLayers, layerMode } = action;
            return Object.assign(Object.assign({}, state), { paths, groupLayers, strokeLayers, planOptions: Object.assign(Object.assign({}, state.planOptions), { selectedStrokeLayers, selectedGroupLayers, layerMode }) });
        case "SET_PROGRESS":
            return Object.assign(Object.assign({}, state), { progress: action.motionIdx });
        case "SET_CONNECTED":
            return Object.assign(Object.assign({}, state), { connected: action.connected });
        default:
            console.warn(`Unrecognized action type '${action.type}'`);
            return state;
    }
}
class WebSerialDriver {
    static connect(port) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!port)
                port = yield navigator.serial.requestPort({ filters: [{ usbVendorId: 0x04D8, usbProductId: 0xFD92 }] });
            // baudRate ref: https://github.com/evil-mad/plotink/blob/a45739b7d41b74d35c1e933c18949ed44c72de0e/plotink/ebb_serial.py#L281
            // (doesn't specify baud rate)
            // and https://pyserial.readthedocs.io/en/latest/pyserial_api.html#serial.Serial.__init__
            // (pyserial defaults to 9600)
            yield port.open({ baudRate: 9600 });
            const { usbVendorId, usbProductId } = port.getInfo();
            return new WebSerialDriver(new EBB(port), `${usbVendorId.toString(16).padStart(4, '0')}:${usbProductId.toString(16).padStart(4, '0')}`);
        });
    }
    name() {
        return this._name;
    }
    constructor(ebb, name) {
        this._unpaused = null;
        this._signalUnpause = null;
        this._cancelRequested = false;
        this.ebb = ebb;
        this._name = name;
    }
    close() {
        return this.ebb.close();
    }
    plot(plan) {
        return __awaiter(this, void 0, void 0, function* () {
            const microsteppingMode = 2;
            this._unpaused = null;
            this._cancelRequested = false;
            yield this.ebb.enableMotors(microsteppingMode);
            let motionIdx = 0;
            let penIsUp = true;
            for (const motion of plan.motions) {
                if (this.onprogress)
                    this.onprogress(motionIdx);
                yield this.ebb.executeMotion(motion);
                if (motion instanceof PenMotion) {
                    penIsUp = motion.initialPos < motion.finalPos;
                }
                if (this._unpaused && penIsUp) {
                    yield this._unpaused;
                    if (this.onpause)
                        this.onpause(false);
                }
                if (this._cancelRequested) {
                    break;
                }
                motionIdx += 1;
            }
            if (this._cancelRequested) {
                yield this.ebb.setPenHeight(Device.Axidraw.penPctToPos(0), 1000);
                if (this.oncancelled)
                    this.oncancelled();
            }
            else {
                if (this.onfinished)
                    this.onfinished();
            }
            yield this.ebb.waitUntilMotorsIdle();
            yield this.ebb.disableMotors();
        });
    }
    cancel() {
        this._cancelRequested = true;
    }
    pause() {
        this._unpaused = new Promise(resolve => {
            this._signalUnpause = resolve;
        });
        if (this.onpause)
            this.onpause(true);
    }
    resume() {
        const signal = this._signalUnpause;
        this._unpaused = null;
        this._signalUnpause = null;
        signal();
    }
    setPenHeight(height, rate) {
        return __awaiter(this, void 0, void 0, function* () {
            if (yield this.ebb.supportsSR()) {
                yield this.ebb.setServoPowerTimeout(10000, true);
            }
            yield this.ebb.setPenHeight(height, rate);
        });
    }
    limp() {
        this.ebb.disableMotors();
    }
}
class SaxiDriver {
    static connect() {
        const d = new SaxiDriver();
        d.connect();
        return d;
    }
    name() {
        return 'Saxi Server';
    }
    close() {
        this.socket.close();
        return Promise.resolve();
    }
    connect() {
        const websocketProtocol = document.location.protocol === "https:" ? "wss" : "ws";
        this.socket = new WebSocket(`${websocketProtocol}://${document.location.host}/chat`);
        this.socket.addEventListener("open", () => {
            console.log(`Connected to EBB server.`);
            this.connected = true;
            if (this.onconnectionchange) {
                this.onconnectionchange(true);
            }
            this.pingInterval = window.setInterval(() => this.ping(), 30000);
        });
        this.socket.addEventListener("message", (e) => {
            const msg = JSON.parse(e.data);
            switch (msg.c) {
                case "pong":
                    {
                        // nothing
                    }
                    break;
                case "progress":
                    {
                        if (this.onprogress != null) {
                            this.onprogress(msg.p.motionIdx);
                        }
                    }
                    break;
                case "cancelled":
                    {
                        if (this.oncancelled != null) {
                            this.oncancelled();
                        }
                    }
                    break;
                case "finished":
                    {
                        if (this.onfinished != null) {
                            this.onfinished();
                        }
                    }
                    break;
                case "dev":
                    {
                        if (this.ondevinfo != null) {
                            this.ondevinfo(msg.p);
                        }
                    }
                    break;
                case "pause":
                    {
                        if (this.onpause != null) {
                            this.onpause(msg.p.paused);
                        }
                    }
                    break;
                case "plan":
                    {
                        if (this.onplan != null) {
                            this.onplan(Plan.deserialize(msg.p.plan));
                        }
                    }
                    break;
                default:
                    {
                        console.log("Unknown message from server:", msg);
                    }
                    break;
            }
        });
        this.socket.addEventListener("error", () => {
            // TODO: something
        });
        this.socket.addEventListener("close", () => {
            console.log(`Disconnected from EBB server, reconnecting in 5 seconds.`);
            window.clearInterval(this.pingInterval);
            this.pingInterval = null;
            this.connected = false;
            if (this.onconnectionchange) {
                this.onconnectionchange(false);
            }
            this.socket = null;
            setTimeout(() => this.connect(), 5000);
        });
    }
    plot(plan) {
        fetch("/plot", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: new Blob([JSON.stringify(plan.serialize())], { type: 'application/json' })
        });
    }
    cancel() {
        fetch("/cancel", { method: "POST" });
    }
    pause() {
        fetch("/pause", { method: "POST" });
    }
    resume() {
        fetch("/resume", { method: "POST" });
    }
    send(msg) {
        if (!this.connected) {
            throw new Error(`Can't send message: not connected`);
        }
        this.socket.send(JSON.stringify(msg));
    }
    setPenHeight(height, rate) {
        this.send({ c: "setPenHeight", p: { height, rate } });
    }
    limp() { this.send({ c: "limp" }); }
    ping() { this.send({ c: "ping" }); }
}
const usePlan = (paths, planOptions) => {
    const [isPlanning, setIsPlanning] = useState(false);
    const [latestPlan, setPlan] = useState(null);
    function serialize(po) {
        return JSON.stringify(po, (k, v) => v instanceof Set ? [...v] : v);
    }
    function attemptRejigger(previousOptions, newOptions, previousPlan) {
        const newOptionsWithOldPenHeights = Object.assign(Object.assign({}, newOptions), { penUpHeight: previousOptions.penUpHeight, penDownHeight: previousOptions.penDownHeight });
        if (serialize(previousOptions) === serialize(newOptionsWithOldPenHeights)) {
            // The existing plan should be the same except for penup/pendown heights.
            return previousPlan.withPenHeights(Device.Axidraw.penPctToPos(newOptions.penUpHeight), Device.Axidraw.penPctToPos(newOptions.penDownHeight));
        }
    }
    const lastPaths = useRef(null);
    const lastPlan = useRef(null);
    const lastPlanOptions = useRef(null);
    useEffect(() => {
        if (!paths) {
            return;
        }
        if (lastPlan.current != null && lastPaths.current === paths) {
            const rejiggered = attemptRejigger(lastPlanOptions.current, planOptions, lastPlan.current);
            if (rejiggered) {
                setPlan(rejiggered);
                lastPlan.current = rejiggered;
                lastPlanOptions.current = planOptions;
                return;
            }
        }
        lastPaths.current = paths;
        const worker = new PlanWorker();
        setIsPlanning(true);
        console.time("posting to worker");
        worker.postMessage({ paths, planOptions });
        console.timeEnd("posting to worker");
        const listener = (m) => {
            console.time("deserializing");
            const deserialized = Plan.deserialize(m.data);
            console.timeEnd("deserializing");
            setPlan(deserialized);
            lastPlan.current = deserialized;
            lastPlanOptions.current = planOptions;
            setIsPlanning(false);
        };
        worker.addEventListener("message", listener);
        return () => {
            worker.terminate();
            worker.removeEventListener("message", listener);
            setIsPlanning(false);
        };
    }, [paths, serialize(planOptions)]);
    return [isPlanning, latestPlan, setPlan];
};
const setPaths = (paths) => {
    const strokes = new Set();
    const groups = new Set();
    for (const path of paths) {
        strokes.add(path.stroke);
        groups.add(path.groupId);
    }
    const layerMode = groups.size > 1 ? 'group' : 'stroke';
    const groupLayers = Array.from(groups).sort();
    const strokeLayers = Array.from(strokes).sort();
    return { type: "SET_PATHS", paths, groupLayers, strokeLayers, selectedGroupLayers: new Set(groupLayers), selectedStrokeLayers: new Set(strokeLayers), layerMode };
};
function PenHeight({ state, driver }) {
    const { penUpHeight, penDownHeight } = state.planOptions;
    const dispatch = useContext(DispatchContext);
    const setPenUpHeight = (x) => dispatch({ type: "SET_PLAN_OPTION", value: { penUpHeight: x } });
    const setPenDownHeight = (x) => dispatch({ type: "SET_PLAN_OPTION", value: { penDownHeight: x } });
    const penUp = () => {
        const height = Device.Axidraw.penPctToPos(penUpHeight);
        driver.setPenHeight(height, 1000);
    };
    const penDown = () => {
        const height = Device.Axidraw.penPctToPos(penDownHeight);
        driver.setPenHeight(height, 1000);
    };
    return React.createElement(Fragment, null,
        React.createElement("div", { className: "flex" },
            React.createElement("label", { className: "pen-label" },
                "up height (%)",
                React.createElement("input", { type: "number", min: "0", max: "100", value: penUpHeight, onChange: (e) => setPenUpHeight(parseInt(e.target.value, 10)) })),
            React.createElement("label", { className: "pen-label" },
                "down height (%)",
                React.createElement("input", { type: "number", min: "0", max: "100", value: penDownHeight, onChange: (e) => setPenDownHeight(parseInt(e.target.value, 10)) }))),
        React.createElement("div", { className: "flex" },
            React.createElement("button", { onClick: penUp }, "pen up"),
            React.createElement("button", { onClick: penDown }, "pen down")));
}
function VisualizationOptions({ state }) {
    const dispatch = useContext(DispatchContext);
    return React.createElement(React.Fragment, null,
        React.createElement("label", { title: "Width of lines in preview. Does not affect plot." },
            "visualized stroke width (mm)",
            React.createElement("input", { type: "number", value: state.visualizationOptions.penStrokeWidth, min: "0", max: "10", step: "0.1", onChange: (e) => dispatch({ type: "SET_VISUALIZATION_OPTION", value: { penStrokeWidth: Number(e.target.value) } }) })),
        React.createElement("label", { className: "flex-checkbox", title: "Color paths in the preview based on the order in which they will be plotted. Yellow is first, pink is last." },
            React.createElement("input", { type: "checkbox", checked: state.visualizationOptions.colorPathsByStrokeOrder, onChange: (e) => dispatch({ type: "SET_VISUALIZATION_OPTION", value: { colorPathsByStrokeOrder: !!e.target.checked } }) }),
            "color based on order"));
}
function SwapPaperSizesButton({ onClick }) {
    return React.createElement("svg", { className: "paper-sizes__swap", xmlns: "http://www.w3.org/2000/svg", width: "14.05", height: "11.46", viewBox: "0 0 14.05 11.46", onClick: onClick },
        React.createElement("g", null,
            React.createElement("polygon", { points: "14.05 3.04 8.79 0 8.79 1.78 1.38 1.78 1.38 4.29 8.79 4.29 8.79 6.08 14.05 3.04" }),
            React.createElement("polygon", { points: "0 8.43 5.26 11.46 5.26 9.68 12.67 9.68 12.67 7.17 5.26 7.17 5.26 5.39 0 8.43" })));
}
function PaperConfig({ state }) {
    const dispatch = useContext(DispatchContext);
    const landscape = state.planOptions.paperSize.isLandscape;
    function setPaperSize(e) {
        const name = e.target.value;
        if (name !== "Custom") {
            const ps = PaperSize.standard[name][landscape ? "landscape" : "portrait"];
            dispatch({ type: "SET_PLAN_OPTION", value: { paperSize: ps } });
        }
    }
    function setCustomPaperSize(x, y) {
        dispatch({ type: "SET_PLAN_OPTION", value: { paperSize: new PaperSize({ x, y }) } });
    }
    const { paperSize } = state.planOptions;
    const paperSizeName = Object.keys(PaperSize.standard).find((psName) => {
        const ps = PaperSize.standard[psName].size;
        return (ps.x === paperSize.size.x && ps.y === paperSize.size.y)
            || (ps.y === paperSize.size.x && ps.x === paperSize.size.y);
    }) || "Custom";
    return React.createElement("div", null,
        React.createElement("select", { value: paperSizeName, onChange: setPaperSize },
            Object.keys(PaperSize.standard).map((name) => React.createElement("option", { key: name }, name)),
            React.createElement("option", null, "Custom")),
        React.createElement("div", { className: "paper-sizes" },
            React.createElement("label", { className: "paper-label" },
                "width (mm)",
                React.createElement("input", { type: "number", value: paperSize.size.x, onChange: (e) => setCustomPaperSize(Number(e.target.value), paperSize.size.y) })),
            React.createElement(SwapPaperSizesButton, { onClick: () => {
                    dispatch({
                        type: "SET_PLAN_OPTION",
                        value: { paperSize: paperSize.isLandscape ? paperSize.portrait : paperSize.landscape }
                    });
                } }),
            React.createElement("label", { className: "paper-label" },
                "height (mm)",
                React.createElement("input", { type: "number", value: paperSize.size.y, onChange: (e) => setCustomPaperSize(paperSize.size.x, Number(e.target.value)) }))),
        React.createElement("div", null,
            React.createElement("label", null,
                "rotate drawing (degrees)",
                React.createElement("div", { className: "horizontal-labels" },
                    React.createElement("img", { src: rotateDrawingIcon, alt: "rotate drawing (degrees)" }),
                    React.createElement("input", { type: "number", min: "-90", step: "90", max: "360", placeholder: "0", value: state.planOptions.rotateDrawing, onInput: (e) => {
                            const value = e.target.value;
                            if (Number(value) < 0) {
                                e.target.value = "270";
                            }
                            if (Number(value) > 270) {
                                e.target.value = "0";
                            }
                        }, onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { rotateDrawing: e.target.value } }) })))),
        React.createElement("label", null,
            "margin (mm)",
            React.createElement("input", { type: "number", value: state.planOptions.marginMm, min: "0", max: Math.min(paperSize.size.x / 2, paperSize.size.y / 2), onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { marginMm: Number(e.target.value) } }) })));
}
function MotorControl({ driver }) {
    return React.createElement("div", null,
        React.createElement("button", { onClick: () => driver.limp() }, "disengage motors"));
}
function PlanStatistics({ plan }) {
    return React.createElement("div", { className: "duration" },
        React.createElement("div", null, "Duration"),
        React.createElement("div", null,
            React.createElement("strong", null, plan && plan.duration ? formatDuration(plan.duration()) : "-")));
}
function TimeLeft({ plan, progress, currentMotionStartedTime, paused }) {
    const [_, setTime] = useState(new Date());
    // Interval that ticks every second to rerender
    // and recalculate time remaining for long motions
    useEffect(() => {
        const interval = setInterval(() => {
            setTime(new Date());
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, [setTime]);
    if (!plan || !plan.duration || progress === null || paused) {
        return null;
    }
    const currentMotionTimeSpent = (new Date().getTime() - currentMotionStartedTime.getTime()) / 1000;
    const duration = plan.duration(progress);
    return React.createElement("div", { className: "duration" },
        React.createElement("div", { className: "time-remaining-label" }, "Time remaining"),
        React.createElement("div", null,
            React.createElement("strong", null, formatDuration(duration - currentMotionTimeSpent))));
}
function PlanPreview({ state, previewSize, plan }) {
    const ps = state.planOptions.paperSize;
    const strokeWidth = state.visualizationOptions.penStrokeWidth * Device.Axidraw.stepsPerMm;
    const colorPathsByStrokeOrder = state.visualizationOptions.colorPathsByStrokeOrder;
    const memoizedPlanPreview = useMemo(() => {
        if (plan) {
            const palette = colorPathsByStrokeOrder
                ? interpolator(colormap({ colormap: 'spring' }))
                : () => 'rgba(0, 0, 0, 0.8)';
            const lines = plan.motions.map((m) => {
                if (m instanceof XYMotion) {
                    return m.blocks.map((b) => b.p1).concat([m.p2]);
                }
                else {
                    return [];
                }
            }).filter((m) => m.length);
            return React.createElement("g", { transform: `scale(${1 / Device.Axidraw.stepsPerMm})` }, lines.map((line, i) => React.createElement("path", { key: i, d: line.reduce((m, { x, y }, j) => m + `${j === 0 ? "M" : "L"}${x} ${y}`, ""), style: i % 2 === 0 ? { stroke: "rgba(0, 0, 0, 0.3)", strokeWidth: 0.5 } : { stroke: palette(1 - i / lines.length), strokeWidth } })));
        }
    }, [plan, strokeWidth, colorPathsByStrokeOrder]);
    // w/h of svg.
    // first try scaling so that h = area.h. if w < area.w, then ok.
    // otherwise, scale so that w = area.w.
    const { width, height } = ps.size.x / ps.size.y * previewSize.height <= previewSize.width
        ? { width: ps.size.x / ps.size.y * previewSize.height, height: previewSize.height }
        : { height: ps.size.y / ps.size.x * previewSize.width, width: previewSize.width };
    const [microprogress, setMicroprogress] = useState(0);
    useLayoutEffect(() => {
        let rafHandle = null;
        let cancelled = false;
        if (state.progress != null) {
            const startingTime = Date.now();
            const updateProgress = () => {
                if (cancelled) {
                    return;
                }
                setMicroprogress(Date.now() - startingTime);
                rafHandle = requestAnimationFrame(updateProgress);
            };
            // rafHandle = requestAnimationFrame(updateProgress)
            updateProgress();
        }
        return () => {
            cancelled = true;
            if (rafHandle != null) {
                cancelAnimationFrame(rafHandle);
            }
            setMicroprogress(0);
        };
    }, [state.progress]);
    let progressIndicator = null;
    if (state.progress != null && plan != null) {
        const motion = plan.motion(state.progress);
        const pos = motion instanceof XYMotion
            ? motion.instant(Math.min(microprogress / 1000, motion.duration())).p
            : plan.motion(state.progress - 1).p2;
        const { stepsPerMm } = Device.Axidraw;
        const posXMm = pos.x / stepsPerMm;
        const posYMm = pos.y / stepsPerMm;
        progressIndicator =
            React.createElement("svg", { width: width * 2, height: height * 2, viewBox: `${-width} ${-height} ${width * 2} ${height * 2}`, style: {
                    transform: `translateZ(0.001px) ` +
                        `translate(${-width}px, ${-height}px) ` +
                        `translate(${posXMm / ps.size.x * 50}%,${posYMm / ps.size.y * 50}%)`
                } },
                React.createElement("g", null,
                    React.createElement("path", { d: `M-${width} 0l${width * 2} 0M0 -${height}l0 ${height * 2}`, style: { stroke: "rgba(222, 114, 114, 0.6)", strokeWidth: 1 } }),
                    React.createElement("path", { d: "M-10 0l20 0M0 -10l0 20", style: { stroke: "rgba(222, 114, 114, 1)", strokeWidth: 2 } })));
    }
    const margins = React.createElement("g", null,
        React.createElement("rect", { x: state.planOptions.marginMm, y: state.planOptions.marginMm, width: (ps.size.x - state.planOptions.marginMm * 2), height: (ps.size.y - state.planOptions.marginMm * 2), fill: "none", stroke: "black", strokeWidth: "0.1", strokeDasharray: "1,1" }));
    return React.createElement("div", { className: "preview" },
        React.createElement("svg", { width: width, height: height, viewBox: `0 0 ${ps.size.x} ${ps.size.y}` },
            memoizedPlanPreview,
            margins),
        progressIndicator);
}
function PlanLoader({ isLoadingFile, isPlanning }) {
    if (isLoadingFile || isPlanning) {
        return React.createElement("div", { className: "preview-loader" }, isLoadingFile ? 'Loading file...' : 'Replanning...');
    }
    return null;
}
function LayerSelector({ state }) {
    const dispatch = useContext(DispatchContext);
    const layers = state.planOptions.layerMode === 'group' ? state.groupLayers : state.strokeLayers;
    const selectedLayers = state.planOptions.layerMode === 'group' ? state.planOptions.selectedGroupLayers : state.planOptions.selectedStrokeLayers;
    if (layers.length <= 1) {
        return null;
    }
    const layersChanged = state.planOptions.layerMode === 'group' ?
        (e) => {
            const selectedLayers = new Set([...e.target.selectedOptions].map((o) => o.value));
            dispatch({ type: "SET_PLAN_OPTION", value: { selectedGroupLayers: selectedLayers } });
        } :
        (e) => {
            const selectedLayers = new Set([...e.target.selectedOptions].map((o) => o.value));
            dispatch({ type: "SET_PLAN_OPTION", value: { selectedStrokeLayers: selectedLayers } });
        };
    return React.createElement("div", null,
        React.createElement("label", null,
            "layers",
            React.createElement("select", { className: "layer-select", multiple: true, value: [...selectedLayers], onChange: layersChanged, size: 3, disabled: state.progress != null }, layers.map((layer) => React.createElement("option", { key: layer }, layer)))));
}
function PlotButtons({ state, plan, isPlanning, driver }) {
    function cancel() {
        driver.cancel();
    }
    function pause() {
        driver.pause();
    }
    function resume() {
        driver.resume();
    }
    function plot(plan) {
        driver.plot(plan);
    }
    return React.createElement("div", null,
        isPlanning
            ? React.createElement("button", { className: "replan-button", disabled: true }, "Replanning...")
            : React.createElement("button", { className: `plot-button ${state.progress != null ? "plot-button--plotting" : ""}`, disabled: plan == null || state.progress != null, onClick: () => plot(plan) }, plan && state.progress != null ? "Plotting..." : "Plot"),
        React.createElement("div", { className: `button-row` },
            React.createElement("button", { className: `cancel-button ${state.progress != null ? "cancel-button--active" : ""}`, onClick: state.paused ? resume : pause, disabled: plan == null || state.progress == null }, state.paused ? "Resume" : "Pause"),
            React.createElement("button", { className: `cancel-button ${state.progress != null ? "cancel-button--active" : ""}`, onClick: cancel, disabled: plan == null || state.progress == null }, "Cancel")));
}
function ResetToDefaultsButton() {
    const dispatch = useContext(DispatchContext);
    const onClick = () => {
        // Clear all user settings that have been saved and reset to the defaults
        window.localStorage.removeItem("planOptions");
        dispatch({ type: "SET_PLAN_OPTION", value: Object.assign({}, defaultPlanOptions) });
    };
    return React.createElement("button", { className: "button-link", onClick: onClick }, "reset all options");
}
function PlanOptions({ state }) {
    const dispatch = useContext(DispatchContext);
    return React.createElement("div", null,
        React.createElement("form", null,
            React.createElement("label", { className: "flex-checkbox", title: "Re-order paths to minimize pen-up travel time" },
                React.createElement("input", { type: "checkbox", checked: state.planOptions.sortPaths, onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { sortPaths: !!e.target.checked } }) }),
                "sort paths"),
            React.createElement("label", { className: "flex-checkbox", title: "Split into layers according to group ID, instead of stroke" },
                React.createElement("input", { type: "checkbox", checked: state.planOptions.layerMode === 'group', onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { layerMode: e.target.checked ? 'group' : 'stroke' } }) }),
                "layer by group"),
            React.createElement("label", { className: "flex-checkbox", title: "Re-scale and position the image to fit on the page" },
                React.createElement("input", { type: "checkbox", checked: state.planOptions.fitPage, onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { fitPage: !!e.target.checked } }) }),
                "fit page"),
            !state.planOptions.fitPage ?
                React.createElement("label", { className: "flex-checkbox", title: "Remove lines that fall outside the margins" },
                    React.createElement("input", { type: "checkbox", checked: state.planOptions.cropToMargins, onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { cropToMargins: !!e.target.checked } }) }),
                    "crop to margins")
                : null),
        React.createElement("div", { className: "horizontal-labels" },
            React.createElement("label", { title: "point-joining radius (mm)" },
                React.createElement("img", { src: pointJoinRadiusIcon, alt: "point-joining radius (mm)" }),
                React.createElement("input", { type: "number", value: state.planOptions.pointJoinRadius, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { pointJoinRadius: Number(e.target.value) } }) })),
            React.createElement("label", { title: "path-joining radius (mm)" },
                React.createElement("img", { src: pathJoinRadiusIcon, alt: "path-joining radius (mm)" }),
                React.createElement("input", { type: "number", value: state.planOptions.pathJoinRadius, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { pathJoinRadius: Number(e.target.value) } }) }))),
        React.createElement("div", null,
            React.createElement("label", { title: "Remove paths that are shorter than this length (in mm)" },
                "minimum path length",
                React.createElement("input", { type: "number", value: state.planOptions.minimumPathLength, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { minimumPathLength: Number(e.target.value) } }) })),
            React.createElement("div", { className: "flex" },
                React.createElement("label", { title: "Acceleration when the pen is down (in mm/s^2)" },
                    "down acc. (mm/s",
                    React.createElement("sup", null, "2"),
                    ")",
                    React.createElement("input", { type: "number", value: state.planOptions.penDownAcceleration, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penDownAcceleration: Number(e.target.value) } }) })),
                React.createElement("label", { title: "Maximum velocity when the pen is down (in mm/s)" },
                    "down max vel. (mm/s)",
                    React.createElement("input", { type: "number", value: state.planOptions.penDownMaxVelocity, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penDownMaxVelocity: Number(e.target.value) } }) }))),
            React.createElement("label", null,
                "cornering factor",
                React.createElement("input", { type: "number", value: state.planOptions.penDownCorneringFactor, step: "0.01", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penDownCorneringFactor: Number(e.target.value) } }) })),
            React.createElement("div", { className: "flex" },
                React.createElement("label", { title: "Acceleration when the pen is up (in mm/s^2)" },
                    "up acc. (mm/s",
                    React.createElement("sup", null, "2"),
                    ")",
                    React.createElement("input", { type: "number", value: state.planOptions.penUpAcceleration, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penUpAcceleration: Number(e.target.value) } }) })),
                React.createElement("label", { title: "Maximum velocity when the pen is up (in mm/s)" },
                    "up max vel. (mm/s)",
                    React.createElement("input", { type: "number", value: state.planOptions.penUpMaxVelocity, step: "0.1", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penUpMaxVelocity: Number(e.target.value) } }) }))),
            React.createElement("div", { className: "flex" },
                React.createElement("label", { title: "How long the pen takes to lift (in seconds)" },
                    "pen lift duration (s)",
                    React.createElement("input", { type: "number", value: state.planOptions.penLiftDuration, step: "0.01", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penLiftDuration: Number(e.target.value) } }) })),
                React.createElement("label", { title: "How long the pen takes to drop (in seconds)" },
                    "pen drop duration (s)",
                    React.createElement("input", { type: "number", value: state.planOptions.penDropDuration, step: "0.01", min: "0", onChange: (e) => dispatch({ type: "SET_PLAN_OPTION", value: { penDropDuration: Number(e.target.value) } }) })))));
}
function PortSelector({ driver, setDriver }) {
    const [initializing, setInitializing] = useState(false);
    useEffect(() => {
        (() => __awaiter(this, void 0, void 0, function* () {
            try {
                const ports = yield navigator.serial.getPorts();
                if (ports.length > 0) {
                    console.log('connecting to', ports[0]);
                    // get the first
                    setDriver(yield WebSerialDriver.connect(ports[0]));
                }
            }
            finally {
                setInitializing(false);
            }
        }))();
    }, []);
    return React.createElement(React.Fragment, null,
        driver ? `Connected: ${driver.name()}` : null,
        !driver ?
            React.createElement("button", { disabled: initializing, onClick: () => __awaiter(this, void 0, void 0, function* () {
                    try {
                        const port = yield navigator.serial.requestPort({ filters: [{ usbVendorId: 0x04D8, usbProductId: 0xFD92 }] });
                        if (driver)
                            yield driver.close();
                        setDriver(yield WebSerialDriver.connect(port));
                    }
                    catch (e) {
                        alert(`Failed to connect to serial device: ${e.message}`);
                        console.error(e);
                    }
                }) }, initializing ? "Connecting..." : (driver ? "Change Port" : "Connect"))
            : null);
}
function Root() {
    const [driver, setDriver] = useState(null);
    const [isDriverConnected, setIsDriverConnected] = useState(false);
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const svgUrl = urlParams.get("svg");
        if (svgUrl) {
            setIsLoadingFile(true);
            setPlan(null);
            fetch(svgUrl)
                .then((response) => {
                if (!response.ok) {
                    throw new Error(`Failed to fetch SVG: ${response.statusText}`);
                }
                return response.text();
            })
                .then((svgString) => {
                dispatch(setPaths(readSvg(svgString)));
            })
                .catch((error) => {
                console.error("Error loading SVG from URL:", error);
                alert("Failed to load SVG from URL. Please check the link and try again.");
            })
                .finally(() => {
                setIsLoadingFile(false);
            });
        }
    }, []);
    useEffect(() => {
        if (isDriverConnected)
            return;
        if (IS_WEB) {
            setDriver(null);
        }
        else {
            setDriver(SaxiDriver.connect());
        }
        setIsDriverConnected(true);
    }, [isDriverConnected]);
    const [state, dispatch] = useReducer(reducer, initialState);
    const [isPlanning, plan, setPlan] = usePlan(state.paths, state.planOptions);
    const [isLoadingFile, setIsLoadingFile] = useState(false);
    useEffect(() => {
        window.localStorage.setItem("planOptions", JSON.stringify(state.planOptions));
    }, [state.planOptions]);
    useEffect(() => {
        if (driver == null)
            return;
        driver.onprogress = (motionIdx) => {
            dispatch({ type: "SET_PROGRESS", motionIdx });
        };
        driver.oncancelled = driver.onfinished = () => {
            dispatch({ type: "SET_PROGRESS", motionIdx: null });
        };
        driver.onconnectionchange = (connected) => {
            dispatch({ type: "SET_CONNECTED", connected });
        };
        driver.ondevinfo = (devInfo) => {
            dispatch({ type: "SET_DEVICE_INFO", value: devInfo });
        };
        driver.onpause = (paused) => {
            dispatch({ type: "SET_PAUSED", value: paused });
        };
        driver.onplan = (plan) => {
            setPlan(plan);
        };
    }, [driver]);
    useEffect(() => {
        const ondrop = (e) => {
            e.preventDefault();
            const item = e.dataTransfer.items[0];
            const file = item.getAsFile();
            const reader = new FileReader();
            setIsLoadingFile(true);
            setPlan(null);
            reader.onload = () => {
                dispatch(setPaths(readSvg(reader.result)));
                document.body.classList.remove("dragover");
                setIsLoadingFile(false);
            };
            reader.onerror = () => {
                setIsLoadingFile(false);
            };
            reader.readAsText(file);
        };
        const ondragover = (e) => {
            e.preventDefault();
            document.body.classList.add("dragover");
        };
        const ondragleave = (e) => {
            e.preventDefault();
            document.body.classList.remove("dragover");
        };
        const onpaste = (e) => {
            e.clipboardData.items[0].getAsString((s) => {
                dispatch(setPaths(readSvg(s)));
            });
        };
        document.body.addEventListener("drop", ondrop);
        document.body.addEventListener("dragover", ondragover);
        document.body.addEventListener("dragleave", ondragleave);
        document.addEventListener("paste", onpaste);
        return () => {
            document.body.removeEventListener("drop", ondrop);
            document.body.removeEventListener("dragover", ondragover);
            document.body.removeEventListener("dragleave", ondragleave);
            document.removeEventListener("paste", onpaste);
        };
    }, []);
    // Each time new motion is started, save the start time
    const currentMotionStartedTime = useMemo(() => {
        return new Date();
    }, [state.progress, plan, state.paused]);
    const previewArea = useRef(null);
    const previewSize = useComponentSize(previewArea);
    const showDragTarget = !plan && !isLoadingFile && !isPlanning;
    return React.createElement(DispatchContext.Provider, { value: dispatch },
        React.createElement("div", { className: `root ${state.connected ? "connected" : "disconnected"}` },
            React.createElement("div", { className: "control-panel" },
                React.createElement("div", { className: `saxi-title red`, title: state.deviceInfo ? state.deviceInfo.path : null },
                    React.createElement("span", { className: "red reg" }, "s"),
                    React.createElement("span", { className: "teal" }, "axi")),
                IS_WEB ? React.createElement(PortSelector, { driver: driver, setDriver: setDriver }) : null,
                !state.connected ? React.createElement("div", { className: "info-disconnected" }, "disconnected") : null,
                React.createElement("div", { className: "section-header" }, "pen"),
                React.createElement("div", { className: "section-body" },
                    React.createElement(PenHeight, { state: state, driver: driver }),
                    React.createElement(MotorControl, { driver: driver }),
                    React.createElement(ResetToDefaultsButton, null)),
                React.createElement("div", { className: "section-header" }, "paper"),
                React.createElement("div", { className: "section-body" },
                    React.createElement(PaperConfig, { state: state }),
                    React.createElement(LayerSelector, { state: state })),
                React.createElement("details", null,
                    React.createElement("summary", { className: "section-header" }, "more"),
                    React.createElement("div", { className: "section-body" },
                        React.createElement(PlanOptions, { state: state }),
                        React.createElement(VisualizationOptions, { state: state }))),
                React.createElement("div", { className: "spacer" }),
                React.createElement("div", { className: "control-panel-bottom" },
                    React.createElement("div", { className: "section-header" }, "plot"),
                    React.createElement("div", { className: "section-body section-body__plot" },
                        React.createElement(PlanStatistics, { plan: plan }),
                        React.createElement(TimeLeft, { plan: plan, progress: state.progress, currentMotionStartedTime: currentMotionStartedTime, paused: state.paused }),
                        React.createElement(PlotButtons, { plan: plan, isPlanning: isPlanning, state: state, driver: driver })))),
            React.createElement("div", { className: "preview-area", ref: previewArea },
                React.createElement(PlanPreview, { state: state, previewSize: { width: Math.max(0, previewSize.width - 40), height: Math.max(0, previewSize.height - 40) }, plan: plan }),
                React.createElement(PlanLoader, { isPlanning: isPlanning, isLoadingFile: isLoadingFile }),
                showDragTarget ? React.createElement(DragTarget, null) : null)));
}
function DragTarget() {
    return React.createElement("div", { className: "drag-target" },
        React.createElement("div", { className: "drag-target-message" }, "Drag SVG here"));
}
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
createRoot(document.getElementById("app")).render(React.createElement(Root, null));
function withSVG(svgString, fn) {
    const div = document.createElement("div");
    div.style.position = "absolute";
    div.style.left = "99999px";
    document.body.appendChild(div);
    try {
        div.innerHTML = svgString;
        const svg = div.querySelector("svg");
        return fn(svg);
    }
    finally {
        div.remove();
    }
}
function readSvg(svgString) {
    return withSVG(svgString, flattenSVG).map((line) => {
        const a = line.points.map(([x, y]) => ({ x, y }));
        a.stroke = line.stroke;
        a.groupId = line.groupId;
        return a;
    });
}
